import type {
  InvalidateQueryFilters,
  QueryClient,
} from "@tanstack/react-query";

/**
 * kubb query keys are structured `[{ url }, ...params]`. Centralise the URL
 * prefixes here so cache / invalidation code references a typed entity instead
 * of hand-writing `[{ url: "/asset" }]` at each call site — which is easy to
 * typo and impossible to find-references on. Add entities as they're adopted.
 */
export const ENTITY_URLS = {
  asset: "/asset",
  assetPosition: "/asset_position",
  assetPositionLink: "/asset_position/link",
  assetRegister: "/asset_register",
  location: "/location",
  scheme: "/scheme",
  company: "/company",
} as const;

export type EntityName = keyof typeof ENTITY_URLS;

/**
 * The kubb query-key prefix for an entity — `[{ url }]`, optionally narrowed by
 * params (`[{ url }, params]`). Matches the shape kubb's get*QueryKey() emits,
 * so it targets the same cache entries.
 */
export function entityKey(
  entity: EntityName,
  params?: Record<string, unknown>,
): readonly [{ url: string }, Record<string, unknown>?] {
  return params
    ? [{ url: ENTITY_URLS[entity] }, params]
    : [{ url: ENTITY_URLS[entity] }];
}

/**
 * Invalidate every cached query for an entity — a typed replacement for the
 * hand-written `invalidateQueries({ queryKey: [{ url: "…" }] })` pattern.
 *
 * Defaults to `refetchType: "all"` so inactive (unmounted) queries refetch too:
 * the app's global QueryClient sets `refetchOnMount: false`, so a plain
 * invalidate would leave an unmounted query on stale data until the next mount.
 */
export function invalidateEntity(
  queryClient: QueryClient,
  entity: EntityName,
  opts?: {
    params?: Record<string, unknown>;
    refetchType?: InvalidateQueryFilters["refetchType"];
  },
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: entityKey(entity, opts?.params),
    refetchType: opts?.refetchType ?? "all",
  });
}
